<template>
  <v-container>
    <div>
      <div v-if="userDevices.loading">
        <v-row justify="center">
          <v-col cols="auto">
            <v-progress-circular
              indeterminate
              size="30"
            />
          </v-col>
        </v-row>
      </div>
      <div
        v-else
        class="d-flex justify-center mb-4"
      >
        <v-btn
          key="action-button-3"
          type="submit"
          color="primary"
          large
          @click="requestUsbDevice()"
        >
          {{ $t("button.pair_new_device") }}
        </v-btn>

        <v-btn
          v-if="devices.length > 0"
          key="action-button-4"
          type="button"
          color="primary"
          :loading="downloading"
          large
          class="ml-4"
          @click="downloadData()"
        >
          <template v-if="!downloading">
            {{ $t("button.download_data") }}
          </template>
          <template v-else>
            <v-progress-circular
              indeterminate
              size="30"
            />
          </template>
        </v-btn>
      </div>

      <v-divider class="my-4" />

      <v-row
        v-if="devices.length > 0"
      >
        <v-col
          v-for="device in devices"
          :key="'insiders-device-' + device.uuid"
          cols="2"
        >
          <device-card
            :device="device"
          />
        </v-col>
      </v-row>

      <div
        v-else
        class="py-12 grey--text text-center"
      >
        {{ $t("insiders_hub.no_devices_paired") }}
      </div>
    </div>
  </v-container>
</template>

<script>

import DeviceCard from '@/components/insiders/DeviceCard'
import JSZip from 'jszip'
import { DeviceApiService } from '@/data/services/DeviceApiService'

export default {
  name: 'InsidersHubView',
  components: { DeviceCard },
  data() {
    return {
      downloading: false,
      devices: [],
      userDevices: {
        payload: [],
        loading: false
      },
      USB_ACK: 0x0A,
      USB_NACK: 0x05,
      USB_CMD_POD_GET_ID: 0x11,
      USB_CMD_POD_DFU: 0x12,
      USB_CMD_POD_GET_VER: 0x13,
      USB_CMD_DATA_READ: 0x31,
      USB_CMD_ERASE_DATA: 0x41
    }
  },
  created() {
    this.fetchDevicesList()
  },
  methods: {
    async fetchDevicesList() {
      try {
        this.userDevices.loading = true

        const resp = await DeviceApiService.getAll()

        this.userDevices.payload = resp.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_devices.error'),
          color: 'error'
        })
      } finally {
        this.userDevices.loading = false
        await this.searchForDevices()
      }
    },
    async requestUsbDevice() {
      try {
        const device = await navigator.usb.requestDevice({
          filters: [
            {
              vendorId: 0x1915
            }
          ]
        })
        if (device != null) {
          await this.searchForDevices()
        }
      } catch (ignored) {
      }
    },
    async searchForDevices() {
      try {
        await navigator.usb.getDevices().then(async (devices) => {
            for (const device of devices) {
              await device.open()

              if (device.configuration === null) {
                await device.selectConfiguration(1)
              }
              await device.claimInterface(0)
              const uuid = await this.getUdid(device)
              const matchingDevice = this.userDevices.payload.find(device => {
                return device.device_name === uuid
              })
              if (matchingDevice) {
                if (this.devices.length === 0) {
                  this.devices.push({
                    webUsbDevice: device,
                    serial_number: matchingDevice.serial_number,
                    uuid: uuid,
                    player: matchingDevice.player != null ? matchingDevice.player.first_name + ' ' + matchingDevice.player.last_name : null
                  })
                } else if (this.devices.length > 0 && !this.devices.some(device => device.uuid === uuid)) {
                  this.devices.push({
                    webUsbDevice: device,
                    serial_number: matchingDevice.serial_number,
                    uuid: uuid,
                    player: matchingDevice.player != null ? matchingDevice.player.first_name + ' ' + matchingDevice.player.last_name : null
                  })
                }
              }
            }
          }
        )
      } catch (e) {
        await this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.device_search.error'),
          color: 'error'
        })
      }
    },
    async downloadData() {
      if (!this.downloading) {
        this.downloading = true
        try {
          const zip = new JSZip()
          for (const device of this.devices) {
            try {
              const datas = await this.dataRead(device.webUsbDevice)
              if (datas !== null) {
                const blob = new Blob([datas.data], { type: 'text/plain' })
                zip.file(device.uuid + '.asi', blob)
                await this.eraseDataFromDevice(device.webUsbDevice)
              }
            } catch (err) {
              await this.$store.dispatch(this.$storeActions.ALERT, {
                text: this.$t('alert.data_download.error'),
                color: 'error'
              })
              this.downloading = false
            }
          }
          if (this.downloading) {
            this.downloading = false
            await this.downloadDkg(zip)
          }
        } catch (e) {
          await this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.data_download.error'),
            color: 'error'
          })
          this.downloading = false
        }
      }
    },
    async dataRead(device) {
      const cmdID = new Uint8Array(1).fill(this.USB_CMD_DATA_READ)
      var dataSize
      let data
      var ack
      await device.transferOut(1, cmdID)
      ack = await device.transferIn(1, 1)
      if (ack.data.getUint8(0) !== this.USB_ACK) {
        return null
      }

      const dataSizeTmp = await device.transferIn(1, 4)
      dataSize = dataSizeTmp.data.getUint32(0)

      if (dataSize !== 0) {
        data = await device.transferIn(1, dataSize)
        await device.transferIn(1, 32)
      }

      return data
    },
    async downloadDkg(zip) {
      if (zip !== null && zip.files !== null) {
        try {
          const zipBlob = await zip.generateAsync({ type: 'blob' })
          const url = URL.createObjectURL(zipBlob)
          const link = document.createElement('a')
          link.href = url
          const currentDate = new Date()
          const year = currentDate.getFullYear()
          const month = String(currentDate.getMonth() + 1).padStart(2, '0') // Months are zero-indexed, so add 1
          const day = String(currentDate.getDate()).padStart(2, '0')

          const formattedDate = `${year}-${month}-${day}`
          link.download = formattedDate + '.dkg'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        } catch (error) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.generate_dkg.error'),
            color: 'error'
          })
        }
      } else {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.download_no_data.error'),
          color: 'error'
        })
      }
    },
    async getUdid(device) {
      const cmdID = new Uint8Array(1).fill(this.USB_CMD_POD_GET_ID)
      var udidBuff
      var ack

      await device.transferOut(1, cmdID)
      ack = await device.transferIn(1, 1)

      if (ack.data.getUint8(0) !== this.USB_ACK) {
        return
      }

      udidBuff = await device.transferIn(1, 8)
      var str = String('')
      for (let i = 0; i < 8; i++) {
        var myNumber = udidBuff.data.getUint8(i)
        var myNumberAsSTR = myNumber.toString(16)

        var formattedNumber = ('0' + myNumberAsSTR).slice(-2)

        str = str + formattedNumber
      }

      return str
    },
    async eraseDataFromDevice(device) {
      const buff = new Uint8Array(1)

      buff[0] = this.USB_CMD_ERASE_DATA
      await device.transferOut(1, buff)
      await device.transferIn(1, 1)
    }
  }
}
</script>
